import api from "./api";

export const resumeApi = {
  // Legacy synchronous upload (keeping for backward compatibility)
  uploadResumes: async (jobId, files) => {
    try {
      const formData = new FormData();
      formData.append('jobId', jobId);
      
      files.forEach(file => {
        formData.append('resumes', file);
      });

      const response = await api.post('/resumes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error in uploadResumes:', error);
      throw new Error('Failed to upload resumes');
    }
  },

  // Optimized batch upload - sends all files in a single request
  uploadResumesBatch: async (jobId, files, onProgress) => {
    try {
      const formData = new FormData();
      formData.append('jobId', jobId);
      
      // Append all files in a single request
      files.forEach(file => {
        formData.append('resumes', file);
      });

      const response = await api.post('/resumes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress({
              type: 'upload',
              progress: percentCompleted,
              message: `Uploading files... ${percentCompleted}%`
            });
          }
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error in uploadResumesBatch:', error);
      throw new Error('Failed to upload resumes batch');
    }
  },

  // New asynchronous upload to storage
  uploadResumesToStorage: async (jobId, files, userId, threadId, callbackUrl) => {
    try {
      const formData = new FormData();
      
      // Append files with the correct field name
      files.forEach(file => {
        formData.append('files[]', file);
      });
      
      // Append metadata
      formData.append('jobId', jobId);
      formData.append('userId', userId);
      if (threadId) formData.append('threadId', threadId);
      if (callbackUrl) formData.append('callbackUrl', callbackUrl);

      const response = await api.post('/resumes/upload-storage', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error in uploadResumesToStorage:', error);
      throw new Error('Failed to upload resumes to storage');
    }
  },

  // Get processing status for a job
  getProcessingStatus: async (jobId) => {
    try {
      const response = await api.get(`/resumes/${jobId}/processing-status`);
      return response.data;
    } catch (error) {
      console.error('Error in getProcessingStatus:', error);
      throw new Error('Failed to get processing status');
    }
  },

  // Get queue statistics
  getQueueStats: async () => {
    try {
      const response = await api.get('/resumes/queue/stats');
      return response.data;
    } catch (error) {
      console.error('Error in getQueueStats:', error);
      throw new Error('Failed to get queue statistics');
    }
  },

  // Import resume from organization (without uploading to blob storage)
  importResumeFromOrganization: async (jobId, resumeData) => {
    try {
      const response = await api.post(`/resumes/${jobId}/import-from-organization`, {
        resumeData
      });
      return response.data;
    } catch (error) {
      console.error('Error in importResumeFromOrganization:', error);
      throw new Error('Failed to import resume from organization');
    }
  },

  // Start queue processor
  startQueueProcessor: async () => {
    try {
      const response = await api.post('/resumes/queue/start');
      return response.data;
    } catch (error) {
      console.error('Error in startQueueProcessor:', error);
      throw new Error('Failed to start queue processor');
    }
  },

  // Stop queue processor
  stopQueueProcessor: async () => {
    try {
      const response = await api.post('/resumes/queue/stop');
      return response.data;
    } catch (error) {
      console.error('Error in stopQueueProcessor:', error);
      throw new Error('Failed to stop queue processor');
    }
  },

  // Retry failed items
  retryFailedItems: async (jobId) => {
    try {
      const response = await api.post('/resumes/queue/retry', { jobId });
      return response.data;
    } catch (error) {
      console.error('Error in retryFailedItems:', error);
      throw new Error('Failed to retry failed items');
    }
  },

  // Clear completed items
  clearCompletedItems: async (jobId) => {
    try {
      const response = await api.delete(`/resumes/queue/clear-completed?jobId=${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error in clearCompletedItems:', error);
      throw new Error('Failed to clear completed items');
    }
  },

  // Get all resumes for a job
  /* Positional (jobId, page, limit, filter), but an options object is accepted
     too — one caller passed { page, limit } and the mistake was invisible: the
     object serialised into the query string, the server read NaN, and every
     page returned the first one. Reading either shape, and coercing to a
     number, means the same slip cannot happen quietly again. */
  getAllResumes: async (jobId, page = 1, limit = 10, filter = 'all') => {
    try {
      const opts = (page && typeof page === 'object') ? page : { page, limit, filter };
      const num = (v, fallback) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
      };
      const query = {
        page: num(opts.page, 1),
        limit: num(opts.limit, 10),
        filter: opts.filter || 'all',
      };
      const response = await api.get(`/resumes/${jobId}`, { params: query });
      console.log('API: Resumes response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getAllResumes:', error);
      console.error('JobId that failed:', jobId);
      throw new Error('Failed to fetch resumes');
    }
  },

  // Get shortlisted resumes
  getShortlistedResumes: async (jobId, page = 1, limit = 10) => {
    try {
      const response = await api.get(`/resumes/${jobId}/shortlisted`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error in getShortlistedResumes:', error);
      throw new Error('Failed to fetch shortlisted resumes');
    }
  },

  // Update resume status
  updateResumeStatus: async (resumeId, type, status, override = false) => {
    try {
      const response = await api.patch(`/resumes/${resumeId}/status`, {
        type,
        status,
        override
      });
      return response.data;
    } catch (error) {
      console.error('Error in updateResumeStatus:', error);
      throw new Error('Failed to update resume status');
    }
  },

  // Delete resume
  deleteResume: async (resumeId) => {
    try {
      const response = await api.delete(`/resumes/${resumeId}`);
      return response.data;
    } catch (error) {
      console.error('Error in deleteResume:', error);
      throw new Error('Failed to delete resume');
    }
  },

  // Get resume details
  getResumeDetails: async (resumeId) => {
    try {
      const response = await api.get(`/resumes/details/${resumeId}`);
      return response.data;
    } catch (error) {
      console.error('Error in getResumeDetails:', error);
      throw new Error('Failed to fetch resume details');
    }
  },

  // Send invite for pre-screening
  sendInvite: async (resumeId, inviteData) => {
    try {
      const response = await api.post(`/resumes/${resumeId}/invite`, inviteData);
      return response.data;
    } catch (error) {
      console.error('Error in sendInvite:', error);
      throw new Error('Failed to send invite');
    }
  },

  // Generate screening questions
  generateScreeningQuestions: async (resumeId) => {
    try {
      const response = await api.post(`/resumes/${resumeId}/generate-questions`);
      return response.data;
    } catch (error) {
      console.error('Error in generateScreeningQuestions:', error);
      throw new Error('Failed to generate questions');
    }
  },

  // Reprocess single resume
  reprocessResume: async (resumeId, jobId = null) => {
    try {
      console.log('Attempting to reprocess resume with ID:', resumeId, 'Job ID:', jobId);
      
      let apiUrl;
      if (jobId) {
        // Use the new endpoint with job context validation
        apiUrl = `/resumes/${jobId}/${resumeId}/reprocess`;
        console.log('API URL (with job context):', apiUrl);
      } else {
        // Fallback to the old endpoint
        apiUrl = `/resumes/${resumeId}/reprocess`;
        console.log('API URL (without job context):', apiUrl);
      }
      
      const response = await api.post(apiUrl);
      console.log('Reprocess response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in reprocessResume:', error);
      console.error('Resume ID that failed:', resumeId);
      console.error('Job ID that failed:', jobId);
      
      // Provide more specific error messages based on the response
      if (error.response) {
        const { status, data } = error.response;
        console.error('Response status:', status);
        console.error('Response data:', data);
        
        if (status === 404) {
          throw new Error(`Resume not found (ID: ${resumeId})`);
        } else if (status === 403) {
          throw new Error(data?.message || 'Resume belongs to a different job');
        } else if (status === 401) {
          throw new Error('Unauthorized - Please log in again');
        } else if (status === 500) {
          throw new Error(data?.message || 'Server error occurred');
        } else {
          throw new Error(data?.message || `HTTP ${status}: Failed to reprocess resume`);
        }
      } else if (error.request) {
        throw new Error('Network error - Please check your connection');
      } else {
        throw new Error(error.message || 'Failed to reprocess resume');
      }
    }
  },

  // Get screening questions
  getScreeningQuestions: async (resumeId) => {
    try {
      const response = await api.get(`/resumes/${resumeId}/questions`);
      return response.data;
    } catch (error) {
      console.error('Error in getScreeningQuestions:', error);
      throw new Error('Failed to fetch questions');
    }
  },

  // Get feedback for resume
  getFeedback: async (resumeId) => {
    try {
      const response = await api.get(`/resumes/${resumeId}/feedback`);
      return response.data;
    } catch (error) {
      console.error('Error in getFeedback:', error);
      throw new Error('Failed to fetch feedback');
    }
  },

  // Reset test attempt for a candidate
  resetTestAttempt: async (resumeId, type) => {
    try {
      const response = await api.post(`/resumes/${resumeId}/reset-attempt`, { type });
      return response.data;
    } catch (error) {
      console.error('Error in resetTestAttempt:', error);
      throw new Error('Failed to reset test attempt');
    }
  },

  // Update HR feedback for final interview
  updateHRFeedback: async (resumeId, feedback) => {
    try {
      const response = await api.put(`/resumes/${resumeId}/hr-feedback`, { feedback });
      return response.data;
    } catch (error) {
      console.error('Error in updateHRFeedback:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update HR feedback');
    }
  },

  // Update test score
  updateTestScore: async (resumeId, jobId, score, type) => {
    try {
      const response = await api.put(`/resumes/${resumeId}/test-score`, { 
        score, 
        type,
        jobId 
      });
      return response.data;
    } catch (error) {
      console.error('Error in updateTestScore:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update test score');
    }
  }
};
